// "Fill in" on the company form: the Worker reads the company's website (/api/company_lookup), the AI proposes
// field values (task extract_company), and this maps its answer onto the form. Nothing is set without a click.
import { z } from "zod";

import { mapSizeToCategory } from "../companies/sizes";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { LabeledValue } from "../types";
import { UNTRUSTED_NOTE, untrusted } from "./ai";

import type {
  NewsItem,
  Signal,
  SignalQuery,
} from "../../../../supabase/functions/_shared/leadAgent.ts";

export { siteExcerpt } from "../../../../supabase/functions/_shared/leadAgent.ts";

export const LOOKUP_ENDPOINT = "/api/company_lookup";

export type CompanyLookup = {
  website: string;
  pages: { url: string; text: string }[];
  /** Addresses the site publishes, its own domain first (Find contacts) */
  emails?: string[];
  linkedin: string[];
  logo?: string | null;
};

/** The website's pages as text, read by the Worker (https only, private hosts refused) */
export const lookupCompany = async (
  website: string,
): Promise<CompanyLookup> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const response = await fetch(LOOKUP_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${data.session?.access_token ?? ""}`,
    },
    body: JSON.stringify({ website }),
    signal: AbortSignal.timeout(30_000),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(body?.error ?? `Lookup failed (${response.status})`);
  return body as CompanyLookup;
};

/** The `items` a Worker reader answers with the session; [] when none or on error */
const workerItems = async <T>(path: string, request: unknown): Promise<T[]> => {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const response = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${data.session?.access_token ?? ""}`,
      },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(15_000),
    });
    const body = await response.json().catch(() => ({}));
    return Array.isArray(body?.items) ? body.items : [];
  } catch {
    return [];
  }
};

/** Recent headlines about a company (Bing News through the Worker): "why now" evidence */
export const recentNews = (query: string) =>
  workerItems<NewsItem>("/api/news", { query });

/** Dated signals beyond the news (job ads, tenders, regulator rules, annual report) through the Worker */
export const recentSignals = (company: SignalQuery) =>
  workerItems<Signal>("/api/signals", company);

const text = z.string().nullish();
/** What the model may answer; unknown keys are dropped by zod */
export const ExtractCompanySchema = z.object({
  website: text,
  description: text,
  sector: text,
  size: z.union([z.number(), z.string()]).nullish(),
  country: text,
  city: text,
  address: text,
  zipcode: text,
  state: text,
  phone: text,
  linkedin_url: text,
  revenue: text,
  tax_identifier: text,
});
export type ExtractCompanyAnswer = z.infer<typeof ExtractCompanySchema>;

/** The company form fields the autofill may propose, in form order */
export const AUTOFILL_FIELDS = [
  "logo",
  "website",
  "linkedin_url",
  "phone_number",
  "sector",
  "size",
  "revenue",
  "tax_identifier",
  "address",
  "city",
  "zipcode",
  "state_abbr",
  "country",
  "description",
] as const;
export type AutofillField = (typeof AUTOFILL_FIELDS)[number];
export type Proposals = Partial<Record<AutofillField, string | number>>;

const MAX_PROMPT_PAGE_CHARS = 15_000;

export const buildExtractPrompt = (
  name: string,
  lookup: CompanyLookup,
  sectors: LabeledValue[],
) =>
  [
    `Extract facts about the company "${name}" from its own website pages below.`,
    "Only give values the pages state or clearly imply; use null when unsure. Do not invent.",
    `sector: one of ${sectors.map((s) => `"${s.label.trim()}"`).join(", ")} (from the pages or what is publicly known about the company), or null.`,
    "size: the number of employees as a number: as stated, else your best estimate from the pages and what is publicly known about the company (a national bank has thousands); null only when you cannot estimate.",
    "description: two sentences on what the company does, in English.",
    "country, state and city in English; address is the street address only; zipcode is the postal code; phone in international format.",
    "Answer with JSON only:",
    '{"website": "...", "description": "...", "sector": "...", "size": 120, "country": "...", "city": "...", "address": "...", "zipcode": "...", "state": "...", "phone": "...", "linkedin_url": "...", "revenue": "...", "tax_identifier": "..."}',
    "",
    UNTRUSTED_NOTE,
    ...lookup.pages.map((page) =>
      untrusted(page.url, page.text.slice(0, MAX_PROMPT_PAGE_CHARS)),
    ),
    lookup.linkedin.length
      ? untrusted("linkedin links on the site", lookup.linkedin.join("\n"))
      : "",
  ]
    .filter(Boolean)
    .join("\n");

const clean = (value: string | null | undefined, max = 300) => {
  const trimmed = value?.trim().replace(/\s+/g, " ");
  return trimmed && trimmed.toLowerCase() !== "null"
    ? trimmed.slice(0, max)
    : undefined;
};

const httpsUrl = (value?: string) => {
  if (!value) return undefined;
  try {
    const url = new URL(
      /^https?:\/\//i.test(value) ? value : `https://${value}`,
    );
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString().replace(/\/$/, "")
      : undefined;
  } catch {
    return undefined;
  }
};

const sizeOf = (value: ExtractCompanyAnswer["size"]) => {
  const n =
    typeof value === "number"
      ? value
      : Number(String(value ?? "").replace(/[^\d].*$/, ""));
  return Number.isFinite(n) && n > 0
    ? mapSizeToCategory(Math.round(n))
    : undefined;
};

/**
 * The logo to propose: the site's own icon (read by the Worker, never the AI's answer), else Apollo's logo, else the
 * favicon service the form already uses (getCompanyAvatar). https only; nothing when the form shows that logo already.
 */
export const logoProposal = (
  website: string | undefined,
  found: unknown,
  current: Record<string, unknown> = {},
): string | undefined => {
  const own =
    typeof found === "string" && /^https:\/\/[^/\s]+\.[^/\s]+\//i.test(found)
      ? found
      : undefined;
  const host = website ? httpsUrl(website)?.replace(/^https?:\/\//, "") : "";
  const src = own ?? (host ? `https://favicon.show/${host}` : undefined);
  const now = (current.logo as { src?: string } | null | undefined)?.src;
  return src && src !== now ? src : undefined;
};

// Which configured sector a map category or industry names, matched by the sector's label (a renamed sector keeps its
// old value: "Banks" may be saved as consumer-discretionary). The first rule whose words match decides; if no sector
// has its label the next rule may still match, except government, which never becomes Financials.
// ponytail: word lists over the category or industry; Fill in on the company corrects a miss.
const SECTOR_RULES: { label: RegExp; words: RegExp; final?: boolean }[] = [
  {
    label: /^(government|public sector)/i,
    words:
      /\b(government|ministry|minist|authority|municipal|customs|council|embassy|public sector|human resources department)\b/i,
    final: true,
  },
  { label: /^banks?$|^banking/i, words: /\b(bank|banque|banking)/i },
  {
    label: /fin ?tech/i,
    words: /\b(fin ?tech|payment|wallet|lending|e-?money)/i,
  },
  { label: /^insurance/i, words: /\b(insurance|insurer|takaful)/i },
  {
    label: /^financ/i,
    words:
      /\b(bank|banque|financ|insurance|insurer|invest|payment|fintech|lending|brokerage|exchange house)/i,
  },
  {
    label: /health/i,
    words: /\b(hospital|medical|clinic|health|pharma|laborator|diagnostic)/i,
  },
  {
    label: /communication|telecom/i,
    words:
      /\b(telecom|mobile network|communications|media|broadcast|internet provider)/i,
  },
  {
    label: /information tech|^it$|technology/i,
    words: /\b(software|information technology|it services)/i,
  },
  {
    label: /utilit/i,
    words: /\b(electric|water|utility|utilities|power (company|plant))/i,
  },
  { label: /energy/i, words: /\b(oil|gas|petroleum|energy)\b/i },
  {
    label: /real estate/i,
    words: /\b(real estate|property|properties|developer)\b/i,
  },
  {
    label: /materials/i,
    words: /\b(cement|steel|chemical|mining|fertili[sz]er|glass|paper)\b/i,
  },
  {
    label: /industr|manufactur/i,
    words:
      /\b(manufactur|factory|vehicle|automotive|industrial|logistics|shipping|construction|engineering|airline|transport)/i,
  },
  {
    label: /consumer staples|food/i,
    words: /\b(food|grocery|beverage|supermarket|tobacco|household)/i,
  },
  {
    label: /consumer discretionary|retail|hospitality/i,
    words: /\b(retail|hotel|resort|restaurant|apparel|fashion|travel)/i,
  },
  { label: /education/i, words: /\b(school|university|education|academy)/i },
];

/** The configured sector (its value) a map category or industry names, or undefined */
export const sectorFor = (
  industry: string | undefined,
  sectors: { value: string; label?: string }[],
) => {
  if (!industry) return undefined;
  for (const rule of SECTOR_RULES) {
    if (!rule.words.test(industry)) continue;
    const sector = sectors.find((s) =>
      rule.label.test((s.label ?? s.value).trim()),
    );
    if (sector) return sector.value;
    if (rule.final) return undefined;
  }
  return undefined;
};

const LOCAL_FIELDS: AutofillField[] = [
  "address",
  "city",
  "zipcode",
  "state_abbr",
  "country",
  "phone_number",
  "revenue",
  "tax_identifier",
];

/** A country as a comparable name: "EG" and "Egypt" are the same */
const countryKey = (value: unknown) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (/^[A-Za-z]{2}$/.test(text))
    try {
      return (
        new Intl.DisplayNames(["en"], { type: "region" })
          .of(text.toUpperCase())
          ?.toLowerCase() ?? text.toLowerCase()
      );
    } catch {
      return text.toLowerCase();
    }
  return text.toLowerCase();
};

const abroad = (known: unknown, proposed: unknown) => {
  const a = countryKey(known);
  const b = countryKey(proposed);
  return !!a && !!b && a !== b;
};

/** The AI answer as form values: configured sectors only, size bucketed, LinkedIn checked, no empty or unchanged values */
export const proposalsFrom = (
  answer: ExtractCompanyAnswer,
  sectors: LabeledValue[],
  current: Record<string, unknown> = {},
): Proposals => {
  // labels first: a renamed sector keeps its old value ("Banks" saved as consumer-discretionary)
  const sectorText = clean(answer.sector)?.toLowerCase();
  const sector =
    (
      sectors.find((s) => s.label.trim().toLowerCase() === sectorText) ??
      sectors.find((s) => s.value.toLowerCase() === sectorText)
    )?.value ?? sectorFor(sectorText, sectors); // Apollo says "banking", the list says "Banks"
  const linkedin = httpsUrl(clean(answer.linkedin_url));
  const all: Proposals = {
    website: httpsUrl(clean(answer.website)),
    linkedin_url:
      linkedin &&
      /^https?:\/\/([a-z]{2,3}\.|www\.)?linkedin\.com\//i.test(linkedin)
        ? linkedin.replace(/^https?:\/\/[^/]+/, "https://www.linkedin.com")
        : undefined,
    phone_number: clean(answer.phone, 40),
    sector,
    size: sizeOf(answer.size),
    revenue: clean(answer.revenue, 60),
    tax_identifier: clean(answer.tax_identifier, 60),
    address: clean(answer.address),
    city: clean(answer.city, 100),
    zipcode: clean(answer.zipcode, 20),
    state_abbr: clean(answer.state, 60),
    country: clean(answer.country, 100),
    description: clean(answer.description, 1000),
  };
  // a known country and a proposal from another one: the data is a parent abroad (chubb.com gave Germany for
  // Chubb Life Egypt), so its address, phone and revenue are not this company's
  if (abroad(current.country, all.country))
    for (const field of LOCAL_FIELDS) delete all[field];
  const proposals: Proposals = {};
  for (const field of AUTOFILL_FIELDS) {
    const value = all[field];
    const now = current[field];
    if (value === undefined) continue;
    if (
      now != null &&
      String(now).trim().toLowerCase() === String(value).toLowerCase()
    )
      continue;
    proposals[field] = value;
  }
  return proposals;
};

/** Fill in from the name alone: the AI names likely websites; each is opened and kept only if its pages name the company */
export const WebsiteGuessSchema = z.object({
  websites: z.array(z.string()).nullish(),
});
export const MAX_WEBSITE_GUESSES = 3;

export const buildWebsitePrompt = (name: string, country?: string) =>
  [
    `Which websites most likely belong to the company named "${name}"${country ? ` (in ${country})` : ""}?`,
    `Give at most ${MAX_WEBSITE_GUESSES} domains of the company's own website, most likely first; no social media, directories or news sites.`,
    "Give an empty list when you do not know. Answer with JSON only:",
    '{"websites": ["example.com"]}',
  ].join("\n");

const LEGAL_WORDS =
  /\b(inc|ltd|llc|llp|plc|corp|co|company|group|holding|holdings|sae|s\.a\.e|gmbh|sa|ag|bv|fze|fzco|the)\b\.?/g;

/** The words that identify the company: lowercase, legal words and punctuation dropped */
const nameWords = (name: string) =>
  name
    .toLowerCase()
    .replace(LEGAL_WORDS, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .split(" ")
    .filter((w) => w.length >= 2);

/** True when the website's pages (or its domain) contain every identifying word of the company name */
export const namesCompany = (name: string, lookup: CompanyLookup) => {
  const words = nameWords(name);
  if (!words.length) return false;
  const haystack = [
    lookup.website,
    ...lookup.pages.map((p) => p.text.slice(0, MAX_PROMPT_PAGE_CHARS)),
  ]
    .join(" ")
    .toLowerCase();
  const squashed = haystack.replace(/[^\p{L}\p{N}]+/gu, "");
  return words.every((w) => haystack.includes(w) || squashed.includes(w));
};

/** Candidate websites without repeats (same host, www. ignored), in the order given */
export const uniqueSites = (sites: (string | null | undefined)[]) => {
  const seen = new Set<string>();
  return sites.flatMap((site) => {
    const url = httpsUrl(clean(site));
    if (!url) return [];
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (seen.has(host)) return [];
    seen.add(host);
    return [host];
  });
};
